<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import MuxLivestreamPlayer from './MuxLivestreamPlayer.vue';
import { getLivestreamHud } from '@/js/core/utils/events-api';

const POLL_INTERVAL_MS = 60000;
const ROTATION_INTERVAL_MS = 12000;

const streams = ref([]);
const activeIndex = ref(0);
const dismissed = ref(false);
const isLoading = ref(false);
let pollTimer = null;
let rotationTimer = null;

const activeStreams = computed(() => {
  return streams.value.filter(stream => {
    const details = stream.livestream_details || {};
    return (
      details.source === 'mux' && details.live_state === 'active' && Boolean(getPlaybackId(details))
    );
  });
});

const currentStream = computed(() => activeStreams.value[activeIndex.value] || null);
const currentDetails = computed(() => currentStream.value?.livestream_details || {});
const currentPlaybackId = computed(() => getPlaybackId(currentDetails.value));
const hasMultipleStreams = computed(() => activeStreams.value.length > 1);
const shouldRender = computed(() => !dismissed.value && Boolean(currentStream.value));
const currentStreamRoute = computed(() => {
  if (currentStream.value?.path) {
    return currentStream.value.path;
  }

  return {
    name: 'event-view',
    params: {
      eventPageSlug:
        currentStream.value?.event_page_slug || currentStream.value?.gsd_service_api_id,
    },
  };
});

function getPlaybackId(details) {
  return details?.playback_ids?.find(playback => playback?.id)?.id || '';
}

async function loadStreams() {
  if (isLoading.value) return;

  isLoading.value = true;

  try {
    const response = await getLivestreamHud();
    streams.value = response?.data || [];
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[LivestreamHud] Could not load active streams.', error);
    }
  } finally {
    isLoading.value = false;
  }
}

function startPolling() {
  loadStreams();
  pollTimer = window.setInterval(loadStreams, POLL_INTERVAL_MS);
}

function stopPolling() {
  if (pollTimer) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
}

function startRotation() {
  if (rotationTimer || activeStreams.value.length < 2) return;

  rotationTimer = window.setInterval(() => {
    activeIndex.value = (activeIndex.value + 1) % activeStreams.value.length;
  }, ROTATION_INTERVAL_MS);
}

function stopRotation() {
  if (rotationTimer) {
    window.clearInterval(rotationTimer);
    rotationTimer = null;
  }
}

watch(
  () => activeStreams.value.map(stream => stream.id).join(','),
  () => {
    if (activeIndex.value >= activeStreams.value.length) {
      activeIndex.value = 0;
    }

    stopRotation();
    startRotation();
  }
);

onMounted(() => {
  startPolling();
  startRotation();
});

onBeforeUnmount(() => {
  stopPolling();
  stopRotation();
});
</script>

<template>
  <aside v-if="shouldRender" class="livestream-hud" aria-label="Active livestream">
    <button
      type="button"
      class="livestream-hud__dismiss"
      aria-label="Dismiss livestream"
      title="Dismiss livestream"
      @click="dismissed = true"
    >
      <span aria-hidden="true">&times;</span>
    </button>

    <MuxLivestreamPlayer
      v-if="currentPlaybackId"
      :playback-id="currentPlaybackId"
      :title="currentStream.title"
      autoplay
      muted
      compact
    />

    <div class="livestream-hud__body">
      <span class="livestream-hud__eyebrow">Live Now</span>
      <RouterLink class="livestream-hud__title" :to="currentStreamRoute">
        {{ currentStream.title }}
      </RouterLink>
      <span v-if="hasMultipleStreams" class="livestream-hud__count">
        {{ activeIndex + 1 }} / {{ activeStreams.length }}
      </span>
    </div>
  </aside>
</template>
